import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Widget from './Widget';
import { listCOTReports } from '../../api/api';

/**
 * COTWidget - Displays Commitment of Traders data
 * 
 * Props:
 * - hideTitle: boolean - hide widget title
 * - symbol: string - filter by symbol/instrument
 * - limit: number - max reports to display
 */
const COTWidget = ({ 
  hideTitle = false, 
  symbol = null,
  limit = 10
}) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSymbol, setSelectedSymbol] = useState(symbol || 'EUR');

  const symbols = ['EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'NZD', 'GOLD', 'SILVER', 'OIL'];

  const fetchCOT = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (selectedSymbol) params.symbol = selectedSymbol;
      const data = await listCOTReports(params);
      const reportsArray = Array.isArray(data) ? data : (data?.results || []);
      setReports(reportsArray.slice(0, limit));
    } catch (err) {
      console.error('Error fetching COT data:', err);
      setError('Failed to load COT data');
    } finally {
      setLoading(false);
    }
  }, [selectedSymbol, limit]);

  useEffect(() => {
    fetchCOT();
  }, [fetchCOT]);

  const formatNumber = (num) => {
    if (num == null) return '-';
    return new Intl.NumberFormat().format(num);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const getChangeColor = (change) => {
    if (change > 0) return 'text-success';
    if (change < 0) return 'text-destructive';
    return 'text-textSecondary';
  };

  return (
    <Widget title="COT Report" hideTitle={hideTitle} className="h-full">
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header with symbol selector */}
        <div className="flex items-center justify-between px-2 py-1 border-b border-borderDefault gap-2">
          <select
            value={selectedSymbol}
            onChange={(e) => setSelectedSymbol(e.target.value)}
            className="text-xs bg-backgroundSurface border border-borderDefault rounded px-2 py-1"
          >
            {symbols.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button
            onClick={fetchCOT}
            disabled={loading}
            className="text-xs text-primary hover:text-primary/80 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {/* COT data */}
        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="p-4 text-center text-destructive text-sm">{error}</div>
          )}
          
          {!error && reports.length === 0 && !loading && (
            <div className="p-4 text-center text-textSecondary text-sm">No COT data available</div>
          )}

          {!error && reports.length > 0 && (
            <div className="p-2">
              {/* Latest report summary */}
              {reports[0] && (
                <div className="mb-3 p-3 bg-secondary/10 rounded-lg">
                  <div className="text-xs text-textSecondary mb-2">
                    As of {formatDate(reports[0].as_of_date || reports[0].cot_date)}
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-xs text-textSecondary">Large Specs</div>
                      <div className="text-sm font-medium text-success">
                        L: {formatNumber(reports[0].large_speculators_long || reports[0].noncomm_longs)}
                      </div>
                      <div className="text-sm font-medium text-destructive">
                        S: {formatNumber(reports[0].large_speculators_short || reports[0].noncomm_shorts)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-textSecondary">Commercials</div>
                      <div className="text-sm font-medium text-success">
                        L: {formatNumber(reports[0].commercials_long || reports[0].comm_longs)}
                      </div>
                      <div className="text-sm font-medium text-destructive">
                        S: {formatNumber(reports[0].commercials_short || reports[0].comm_shorts)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-textSecondary">Small Specs</div>
                      <div className="text-sm font-medium text-success">
                        L: {formatNumber(reports[0].small_speculators_long || reports[0].nonrep_longs)}
                      </div>
                      <div className="text-sm font-medium text-destructive">
                        S: {formatNumber(reports[0].small_speculators_short || reports[0].nonrep_shorts)}
                      </div>
                    </div>
                  </div>

                  {/* Net positions */}
                  <div className="mt-3 pt-2 border-t border-borderDefault">
                    <div className="text-xs text-textSecondary mb-1">Net Positions</div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className={getChangeColor(reports[0].large_speculators_net || reports[0].noncomm_net)}>
                        <span className="text-sm font-medium">
                          {formatNumber(reports[0].large_speculators_net || reports[0].noncomm_net)}
                        </span>
                      </div>
                      <div className={getChangeColor(reports[0].commercials_net || reports[0].comm_net)}>
                        <span className="text-sm font-medium">
                          {formatNumber(reports[0].commercials_net || reports[0].comm_net)}
                        </span>
                      </div>
                      <div className={getChangeColor(reports[0].small_speculators_net || reports[0].nonrep_net)}>
                        <span className="text-sm font-medium">
                          {formatNumber(reports[0].small_speculators_net || reports[0].nonrep_net)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Historical data table */}
              <div className="text-xs">
                <table className="w-full">
                  <thead>
                    <tr className="text-textSecondary border-b border-borderDefault">
                      <th className="py-1 text-left">Date</th>
                      <th className="py-1 text-right">Net Spec</th>
                      <th className="py-1 text-right">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((report, idx) => {
                      const net = report.large_speculators_net || report.noncomm_net || 0;
                      const prevNet = reports[idx + 1]?.large_speculators_net || reports[idx + 1]?.noncomm_net || net;
                      const change = net - prevNet;
                      return (
                        <tr key={report.id || idx} className="border-b border-borderDefault/50">
                          <td className="py-1">{formatDate(report.as_of_date || report.cot_date)}</td>
                          <td className={`py-1 text-right ${getChangeColor(net)}`}>
                            {formatNumber(net)}
                          </td>
                          <td className={`py-1 text-right ${getChangeColor(change)}`}>
                            {change > 0 ? '+' : ''}{formatNumber(change)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </Widget>
  );
};

COTWidget.propTypes = {
  hideTitle: PropTypes.bool,
  symbol: PropTypes.string,
  limit: PropTypes.number,
};

export default COTWidget;
